import React, { useEffect, useState } from 'react';
import toast from 'lib/utils/toast';
import { addReportPublicParams, report } from 'lib/utils/dataReport';
import { ButtonStatus, ACT_AWARD_TYPE, AWARD_TYPES, AWARD_PRIZE_TYPE } from 'lib/constants/award';
import { useLimitedActivityStoreContext } from 'lib/components/LimitedActivityStoreContext';
import { LIMITED_ACTIVITY_TYPE } from 'lib/constants/common';
import { ATTA_REPORT, ATTA_LIMITED_TIME_ACT } from 'lib/constants/atta';
import { useUserOrderStore } from 'lib/store/userOrderStore';
import { useAwardStatus } from 'lib/hooks/useAwardStatus';
import { useMobileStore } from 'lib/store/mobileStore';
import { useMobileOrder } from 'lib/hooks/useMobileOrder';
import { showOrderMobileInput } from 'lib/components/MobileInput';
import { getGiftBtnType, validMobile } from 'lib/utils/util';
import awardPrizeServiceV2 from 'lib/services/awardPrizeServiceV2';
import './styles.scss';

export default function ReceiveButtonV2({ data, commonStyle }) {
  const {
    returnEndMonth,
    activityId,
    receivedBtnImg,
    tobeReceiveBtnImg,
    toCheckBtnImg,
    toCompleteBtnImg,
    inProgressBtnImg,
    expiredBtnImg,
    emptyBtnImg,
    prizeType,
    actAwardType,
    showMobileInputModal,
  } = data;

  const { userOrderInfo } = useUserOrderStore((state) => ({
    userOrderInfo: state.userOrderInfo,
  }));
  const { isSubscriber, orderTime } = userOrderInfo;
  const mobileInput = useMobileStore((state) => state.inputMobile);
  const { orderFlowPackage } = useMobileOrder();
  const [buttonStatus, setButtonStatus] = useState(ButtonStatus.TO_COMPLETE);
  const useLimitedActivityStore = useLimitedActivityStoreContext();
  const durationInMonths = useLimitedActivityStore((state) => state.durationInMonths);
  const limitedActivityType = useLimitedActivityStore((state) => state.limitedActivityType);
  const { prizeInfo, isReceived, isPoolEmpty, fetchPrizeList: updateReceiveStatus } = useAwardStatus({
    activityId,
    actAwardType,
  });

  // 整合按钮状态对应的处理函数和图片
  const buttonConfig = {
    [ButtonStatus.TO_COMPLETE]: {
      img: toCompleteBtnImg,
      handler: () => {
        addReportPublicParams({
          orderTriggerType: getGiftBtnType(AWARD_TYPES.LIMITED_RETAIN),
        });
        report(ATTA_LIMITED_TIME_ACT.ORDER, {
          mobile: mobileInput,
        });
        if (showMobileInputModal || !validMobile(mobileInput)) {
          showOrderMobileInput();
          return;
        }
        // 这里传0代表无安慰礼包
        orderFlowPackage(0);
      },
    },
    [ButtonStatus.IN_PROGRESS]: {
      img: inProgressBtnImg,
      handler: () => {
        toast.info('当前还不满足条件，请下个月1号再来哦~');
      },
    },
    [ButtonStatus.TOBE_RECEIVE]: {
      img: tobeReceiveBtnImg,
      handler: () => {
        report(ATTA_REPORT.LIMIT_ACTIVITY.RECEIVE_CLICK, {
          button_type: AWARD_TYPES.LIMITED_RETAIN,
        });
        // 延迟更新领取状态，避免后端数据未及时更新导致领取状态不准确
        const receiveCallback = () => {
          setTimeout(() => {
            updateReceiveStatus();
          }, 500);
        };
        if (actAwardType === ACT_AWARD_TYPE.LOTTERY) {
          awardPrizeServiceV2.limitedActLottery({
            actId: activityId,
            drawResult: prizeInfo,
            successCallback: receiveCallback,
          });
          return;
        }
        awardPrizeServiceV2.receiveLimitedTaskGift({
          actId: activityId,
          prizeType,
          successCallback: () => {
            receiveCallback();
          },
        });
      },
    },
    [ButtonStatus.RECEIVED]: {
      img: receivedBtnImg,
      handler: () => {},
    },
    [ButtonStatus.TOBE_CHECK]: {
      img: toCheckBtnImg,
      handler: () => {
        // 必得可以选取表单配置，抽奖只能选取抽奖结果,需要跟后端确认是否所有抽奖的都会返回prizetype，不返回是否有问题
        awardPrizeServiceV2.checkPrizeDetail({
          prizeInfo,
          prizeType: actAwardType === ACT_AWARD_TYPE.LOTTERY ? prizeInfo.prizeType : prizeType,
        });
      },
    },
    [ButtonStatus.EXPIRED]: {
      img: expiredBtnImg,
      handler: () => {
        toast.info('奖励长时间未领已过期，可以参加其他任务领好礼哦～');
      },
    },
    [ButtonStatus.EMPTY]: {
      img: emptyBtnImg,
      handler: () => {
        toast.info('奖励已被领完，可以参加其他任务领好礼哦～');
      },
    },
  };

  useEffect(() => {
    const autoRenewalMonth = durationInMonths - 1; // 续订月份，如连续订购2个月，代表续订1个月
    // 判断是否已订购
    if (!isSubscriber) {
      setButtonStatus(ButtonStatus.TO_COMPLETE);
      return;
    }
    if (autoRenewalMonth < returnEndMonth) {
      setButtonStatus(ButtonStatus.IN_PROGRESS);
      return;
    }
    if (isReceived) {
      const isCdkey = prizeInfo?.prizeType === AWARD_PRIZE_TYPE.CDKEY;
      setButtonStatus(isCdkey ? ButtonStatus.TOBE_CHECK : ButtonStatus.RECEIVED);
      return;
    }
    if (isPoolEmpty) {
      setButtonStatus(ButtonStatus.EMPTY);
      return;
    }
    const isNewUserOnly = limitedActivityType === LIMITED_ACTIVITY_TYPE.USER_ORDER_TIME_NEW;
    if (isNewUserOnly && autoRenewalMonth > returnEndMonth) {
      setButtonStatus(ButtonStatus.EXPIRED);
      return;
    }
    setButtonStatus(ButtonStatus.TOBE_RECEIVE);
    return;
  }, [
    orderTime,
    returnEndMonth,
    isSubscriber,
    isReceived,
    isPoolEmpty,
    prizeInfo,
    limitedActivityType,
    durationInMonths,
    activityId,
    prizeType,
  ]);

  const handleReceiveGift = async () => {
    const config = buttonConfig[buttonStatus];
    config?.handler();
  };

  const buttonImg = buttonConfig[buttonStatus]?.img;

  return (
    <div className="receive-v2-btn" id={`gift-${activityId}`} style={commonStyle} onClick={handleReceiveGift}>
      <img className="btn-img" src={buttonImg} />
    </div>
  );
}
